<template>
  <content-subpage
    class="annotation-list-item-content-subpage"
    :visible="visible"
    :titles="titles"
    :show-content-title="false"
    @content-subpage-hidden="handleClose"
  >
    <template #main-content>
      <div v-if="!isLoading" class="annotation-content">
        <div class="name">Card Details</div>
        <div class="actions">
          <!-- add to notebook button -->
          <cv-button
            v-if="showAddToNotebook"
            kind="ghost"
            size="small"
            class="icon-button add-to-notebook"
            :disabled="!isViewMode"
            @click.stop="handleAddToNotebook"
          >
            <cv-tooltip
              alignment="end"
              direction="bottom"
              :tip="t('notebook_add_to_notebook_label')"
            >
              <svg-icon name="NotebookAdd24" kind="action" />
            </cv-tooltip>
          </cv-button>
          <overflow-menu v-if="showOverflowMenu" :flip-menu="true">
            <OverflowMenuItem :disabled="!canDelete" @click="handleDelete">
              <svg-icon name="Trash24" kind="action" />
              {{ t("button_delete") }}
            </OverflowMenuItem>
            <OverflowMenuItem
              v-if="showEnableCommentOption"
              :disabled="visibleByStudents || !isViewMode"
              @click="handleToggleAnnotationComment(true)"
            >
              <svg-icon name="Comment24" kind="action" />
              {{ t("enable_comment_label") }}
            </OverflowMenuItem>
            <OverflowMenuItem
              v-if="showDisableCommentOption"
              :disabled="visibleByStudents || !isViewMode"
              @click="handleToggleAnnotationComment(false)"
            >
              <svg-icon name="CommentDisabled24" kind="action" />
              {{ t("disable_comment_label") }}
            </OverflowMenuItem>
          </overflow-menu>
        </div>

        <div class="timestamp">
          <div class="timestamp-wrapper">
            <div v-if="showVisibleToStudentsLabel" class="visible-to-students-tag">
              <label-component
                kind="visible-to-students"
                :text="t('annotation_visible_to_students_tag_label')"
                hide-title
              />
            </div>
            <div v-if="isTeamAnnotation" class="team-tag">
              <keywords :tags="t('annotation_team_tag_label')" />
            </div>
            <div class="created-date">
              <span class="label">{{ t("annotation_created_date_label") }}</span>
              <time>{{ `${createdDate}` }}</time>
            </div>
          </div>
          <comment-summary-info
            :resource-uuid="params.annotationUuid"
            :resource-type="commentResourceType"
          />
        </div>
        <div v-if="isTooltipAnnotation && !showMediaObject" class="tooltip-content">
          <h5 class="title">{{ tooltipContentTitle }}</h5>
          <p class="description">{{ tooltipContent }}</p>
        </div>
        <div v-else-if="!isActivityNote" class="content">
          <div v-if="showAnnotationTitle" class="title">{{ annotationTitle }}</div>
          <div class="highlight-content" :class="listItemClasses">
            <media-component
              v-if="hasMO"
              ref="media"
              :cue-points="cuePoints"
              type="mo"
              :uuid="mo?.uuid"
              :mediatype="mo?.fileType"
              :is-ugc="isUgc"
              :annotatable="false"
              :hide-controls="false"
              :zoom-tools="true"
              :action-tools="true"
              :hide-star-button="true"
              :is-transcript-enabled="isTranscriptEnabled"
              :render-transcript="isTranscriptEnabled"
              :is-from-subpage="true"
              :transcript-start-time="transcriptStartTime"
              :transcription-file-version="transcriptionFileVersion"
              :parent-anchor="params.anchor"
              :annotation-anchor="annotation?.anchor"
              :is-unavailable="isMOUnavailable"
              :show-image-annotation="true"
              :annotation="annotation"
              @media-loaded="handleMediaLoaded"
            />
            <rich-text v-else :content="content" :expandable="false" :config="richTextConfig" />
          </div>
        </div>
      </div>
    </template>
    <template #sub-content>
      <annotation-edit
        v-if="isEditMode || isCreateMode"
        ref="annotationEdit"
        :get-mentionable-student-list="params.getMentionableStudentList"
        :get-mentionable-teacher-list="params.getMentionableTeacherList"
        :include-buddy-list-mentions="params.includeBuddyListMentions"
        :annotation="annotation"
        :question="params.question"
        :is-create-mode="isCreateMode"
        :annotation-type="params.annotationType ?? annotation?.type"
        :initial-option-type="params.initialOptionType"
      />
      <annotation-view
        v-else
        :can-edit="isEditable"
        :tooltip-data="params.tooltipData"
        :annotation="annotation"
      />
      <comment-section
        ref="commentSection"
        :params="commentProps"
        :get-mentionable-student-list="params.getMentionableStudentList"
        :get-mentionable-teacher-list="params.getMentionableTeacherList"
        :include-buddy-list-mentions="params.includeBuddyListMentions"
        :viewed-user-id="params.viewedUserId"
        @edit-comment="handleEditComment"
      />
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import mediaObjectApi from "@apis/media.object.api.ts";
import ugcApi from "@apis/ugc.api.ts";
import AnnotationUtils from "@modules/annotation/service/annotation.utils";
import {
  ANNOTATION_MODE,
  ANNOTATION_RESOURCE_TYPE,
  ANNOTATION_TYPE,
  DRAFT_ANNOTATION_UUID,
  NAMESPACE,
  OWNER_TYPE,
} from "../service/annotation.constant";
import { COMMENT_RESOURCE_TYPE } from "@plugins/comment/comment.constant";
import DateTimeFormatter from "@utils/datetime.formatter";
import CommentSummaryInfo from "@plugins/comment/components/CommentSummaryInfo.vue";
import CommentProps from "@plugins/comment/comment.props";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import type { AnnotationContext } from "@modules/annotation/types.ts";
import Keywords from "@plugins/keywords/components/Keywords.vue";
import { isDefined, isEmpty, isNull } from "@utils/var.check";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import AddToNotebookSubpage from "@modules/notebook-editor/components/AddToNotebookSubpage.vue";
import AnnotationView from "@modules/annotation/components/AnnotationView.vue";
import AnnotationEdit from "@modules/annotation/components/AnnotationEdit.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import OverflowMenu from "@plugins/common/components/OverflowMenu.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import RichTextUtils from "@plugins/richtext/utils/richtext.utils";
import { computed, nextTick, onMounted, provide, ref, useTemplateRef, watch } from "vue";
import CommentSection from "@plugins/comment/components/CommentSection.vue";
import { useI18n } from "vue-i18n";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import { useStore } from "vuex";
import htmlSanitizer from "@/plugins/common/service/html.sanitizer";
import { useRoute, useRouter } from "vue-router";

interface Props {
  params?: object;
}

interface VisibleToStudentsParams {
  annotationContext?: Pick<AnnotationContext, "isTeacherView">;
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({}),
});

const { t } = useI18n();

const modal = useModal();
const toast = useToast();
const route = useRoute();
const router = useRouter();
const store = useStore();
const contentSubpageInstance = useContentSubpageInstance();
const { passiveApiFailHandler } = useErrorHandler();
const adapter = useAnnotationAttemptAdapter();
const {
  getAnnotation,
  isTeacherView,
  isStudentView,
  isPreviewMode,
  getSelectedAnnotation,
  handleToggleComment,
  deleteAnnotation,
  setSelectedAnnotation,
  getUgcByAnnotation,
  getMediaByAnnotation,

  loadTimestampAnnotation,
} = useAnnotation(undefined, adapter);
const { canEditAnnotation: canEditExistingAnnotation, isEditorContext } = useAnnotationPermissions(
  props.params?.annotationContext ?? null,
);

const annotationUuid = ref(props.params?.annotationUuid || "");

const { visible, titles, actionHidden, defaultExpose } = useContentSubpage(
  props.params,
  annotationUuid,
);

provide("richTextContext", props.params?.richTextContext);
provide("annotationContext", props.params?.annotationContext);

const isLoading = ref(true);
const commentResourceType = ref(COMMENT_RESOURCE_TYPE.ANNOTATION);
const mo = ref(null);
const moRichtext = ref("");
const isUgc = ref(false);
const cuePoints = ref({});
const transcriptionFileVersion = ref("");
const hasMO = ref(false);
const isMOUnavailable = ref(false);

const media = useTemplateRef("media");
const annotationEdit = useTemplateRef<InstanceType<typeof AnnotationEdit>>("annotationEdit");
const commentSection = useTemplateRef<InstanceType<typeof CommentSection>>("commentSection");

const annotation = computed(() => {
  return getAnnotation(annotationUuid.value);
});

const mode = computed(() => {
  return getSelectedAnnotation.value?.mode;
});

const isViewMode = computed(() => {
  return mode.value === ANNOTATION_MODE.VIEW;
});

const isEditMode = computed(() => {
  return mode.value === ANNOTATION_MODE.EDIT;
});

const isCreateMode = computed(() => {
  return mode.value === ANNOTATION_MODE.CREATE;
});

const isDraft = computed(() => {
  return annotationUuid.value === DRAFT_ANNOTATION_UUID;
});

const isTranscriptEnabled = computed(() => {
  const settings = isDraft.value
    ? props.params
    : RichTextUtils.getMediaObjectFromContent(moRichtext.value)[0];
  return (
    settings !== undefined &&
    "isTranscriptEnabled" in settings &&
    settings.isTranscriptEnabled === true
  );
});

const richTextConfig = computed(() => {
  return {
    plugins: {},
  };
});

const transcriptStartTime = computed(() => {
  const annotated = annotation.value?.data?.highlighterSelectors?.[0];
  if (annotated?.start === undefined) {
    return 0;
  }
  return Math.ceil(annotated?.start) / 1000;
});

const content = computed(() => {
  if (!annotation.value) return "";
  return annotation.value.type === ANNOTATION_TYPE.IMAGE_ANNOTATION
    ? imageHighlightContent.value
    : highlightedContent.value;
});

const imageHighlightContent = computed(() => {
  return AnnotationUtils.wrappedContent(
    t("annotation_image_placeholder"),
    annotation.value.data.highlighterClass,
  );
});

const highlightedContent = computed(() => {
  const content = AnnotationUtils.getHighlightedContent(
    annotation.value.data.highlightedContent,
    annotation.value.data.highlighterClass,
  );
  const txt = document.createElement("textarea");
  txt.innerHTML = content;
  return htmlSanitizer.sanitizeHtmlForRichText(txt.value, getWhitelistHtmlTags.value);
});

const isEditable = computed(() => {
  if (!annotation.value) {
    return false;
  }

  return canEditExistingAnnotation(annotation.value, {
    canEdit: true,
    isTeacherView: isTeacherView.value && !isStudentView.value,
    viewOnly: isPreviewMode.value || !isViewMode.value,
  });
});

const isActivityNote = computed(() => {
  return annotation.value ? annotation.value.type === ANNOTATION_TYPE.ACTIVITY_NOTE : false;
});

const isTimestampAnnotation = computed(() => {
  return annotation.value ? annotation.value.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION : false;
});

const isImageAnnotation = computed(() => {
  return annotation.value ? annotation.value.type === ANNOTATION_TYPE.IMAGE_ANNOTATION : false;
});

const createdDate = computed(() => {
  return annotation.value?.createdDate
    ? DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(annotation.value.createdDate)
    : "-";
});

const allowComment = computed(() => {
  return annotation.value ? annotation.value.allowComment : true;
});

const numberOfComments = computed(() => {
  return isDefined(props.params.numberOfComments) ? props.params.numberOfComments : 0;
});

const commentProps = computed(() => {
  return new CommentProps({
    autoFocus: props.params.autoFocus,
    resourceUuid: props.params.annotationUuid,
    resourceType: "ANNOTATION",
    readOnly:
      props.params.isInNotebook || (annotation.value ? !annotation.value.allowComment : false),
    freezeInteraction: !canComment.value,
    allowComment: allowComment.value,
    numberOfComments: numberOfComments.value,
    editable: true,
    deletable: !props.params.isInNotebook && props.params.canDeleteComment,
    ownerRestricted: isEditorContext.value,
    visibleByStudents: visibleByStudents.value,
    commentIcon: "Plus24",
    commentButtonKind: "tertiary",
    showComment: !isDraft.value,
  });
});

const isCommentEnabled = computed(() => {
  return props.params.canComment;
});

const isInNotebook = computed(() => {
  return props.params.isInNotebook;
});

const showAddToNotebook = computed(() => {
  if (isInNotebook.value || isEditorContext.value) {
    return false;
  }

  if (!isEmpty(props.params.showAddToNotebook)) {
    return props.params.showAddToNotebook;
  }
  return isStudentView.value && !adapter.isEAssessment.value;
});

const showOverflowMenu = computed(() => {
  if (isInNotebook.value) {
    return false;
  }

  return props.params.canDelete || showEnableCommentOption.value || showDisableCommentOption.value;
});

const canComment = computed(() => {
  if (isInNotebook.value || isDraft.value) {
    return false;
  }
  return props.params.canComment && !isCreateMode.value;
});

const canDelete = computed(() => {
  return props.params.canDelete && isViewMode.value;
});

const canToggleComment = computed(() => {
  return props.params.canToggleComment;
});

const showEnableCommentOption = computed(() => {
  return canToggleComment.value && !visibleByStudents.value && !allowComment.value;
});

const showDisableCommentOption = computed(() => {
  return (
    isCommentEnabled.value &&
    canToggleComment.value &&
    !visibleByStudents.value &&
    allowComment.value
  );
});

const isTeamAnnotation = computed(() => {
  return annotation.value?.ownerType === OWNER_TYPE.TEAM;
});

const isTooltipAnnotation = computed(() => {
  return props.params.tooltipData;
});

const tooltipContentTitle = computed(() => {
  return props.params.tooltipData.title;
});

const tooltipContent = computed(() => {
  return props.params.tooltipData.content;
});

const showAnnotationTitle = computed(() => {
  return (
    (isImageAnnotation.value || isTimestampAnnotation.value) &&
    !isNull(annotation.value?.title) &&
    !showMediaObject.value
  );
});

const annotationTitle = computed(() => {
  return isNull(annotation.value?.title) ? "" : annotation.value?.title;
});

const visibleByStudents = computed(() => {
  return annotation.value?.visibleByStudents;
});

const showVisibleToStudentsLabel = computed(() =>
  Boolean(
    visibleByStudents.value &&
    (props.params as VisibleToStudentsParams).annotationContext?.isTeacherView,
  ),
);

const showMediaObject = computed(() => {
  return !isNull(mo.value);
});

const getWhitelistHtmlTags = computed<string[] | undefined>(
  () => store.getters[`${NAMESPACE.APP}/getWhitelistHtmlTags`],
);
watch(mode, (newMode, oldMode) => {
  if (newMode === ANNOTATION_MODE.EDIT && oldMode === ANNOTATION_MODE.VIEW) {
    // check annotation enter edit mode, close editing comment
    commentSection.value.cancelComment();
  }
});

const handleEditComment = () => {
  if (isEditMode.value) {
    // close annotation edit if comment editing
    annotationEdit.value.handleCancel();
  }
};

const handleMediaLoaded = () => {
  if (isTimestampAnnotation.value) {
    nextTick(() => {
      loadTimestampAnnotation(annotation.value, true);
    });
  }
};

const loadMediaAnnotation = () => {
  if (
    annotation.value?.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION ||
    annotation.value?.type === ANNOTATION_TYPE.IMAGE_ANNOTATION
  ) {
    if (isDraft.value) {
      renderDraftMediaAnnotation();
      hasMO.value = true;
      return;
    }

    if (annotation.value?.annotationResourceType === ANNOTATION_RESOURCE_TYPE.QUESTION_ANSWER) {
      renderUgcAnnotation();
    } else {
      renderMOAnnotation();
    }
    hasMO.value = true;
  } else {
    hasMO.value = false;
    isLoading.value = false;
  }
};

const handleDraftMediaLoaded = (fileType: string, uuid: string, isDraftUgc: boolean) => {
  mo.value = {
    uuid,
    fileType,
    content: "",
    transcriptionFileVersion: "",
  };
  moRichtext.value = "";
  isUgc.value = isDraftUgc;
  const annotated = annotation.value?.data?.highlighterSelectors?.[0];
  cuePoints.value = {
    start: Math.floor((annotated?.start ?? 0) / 1000),
  };
  transcriptionFileVersion.value = "";
  isLoading.value = false;
};

const handleDraftMediaUnavailable = (isDraftUgc: boolean) => {
  mo.value = {};
  isMOUnavailable.value = true;
  isUgc.value = isDraftUgc;
  isLoading.value = false;
  passiveApiFailHandler();
};

const renderDraftMediaAnnotation = () => {
  const mediaUuid = annotation.value?.mediaUuid;
  const isUgc =
    annotation.value?.annotationResourceType === ANNOTATION_RESOURCE_TYPE.QUESTION_ANSWER;

  if (!mediaUuid) {
    hasMO.value = false;
    isLoading.value = false;
    return;
  }

  const requestData = {
    uuid: mediaUuid,
  };

  if (isUgc) {
    ugcApi.getMetaData(requestData).then(
      (response) => {
        if (!response || response.code === "error" || !response.data) {
          handleDraftMediaUnavailable(true);
          return;
        }

        handleDraftMediaLoaded(response.data.fileType ?? "", response.data.uuid ?? mediaUuid, true);
      },
      () => {
        handleDraftMediaUnavailable(true);
      },
    );
    return;
  }

  mediaObjectApi.getMetaData(requestData).then(
    (response) => {
      if (!response || response.code === "error" || !response.data) {
        handleDraftMediaUnavailable(false);
        return;
      }

      handleDraftMediaLoaded(response.data.fileType ?? "", response.data.uuid ?? mediaUuid, false);
    },
    () => {
      handleDraftMediaUnavailable(false);
    },
  );
};

const renderUgcAnnotation = () => {
  getUgcByAnnotation(annotation.value).then(
    (data) => {
      mo.value = data;
      moRichtext.value = data.content;
      isUgc.value = true;
      const annotated = annotation.value?.data?.highlighterSelectors?.[0];
      cuePoints.value = {
        start: Math.floor(annotated?.start / 1000),
      };
      transcriptionFileVersion.value = data.transcriptionFileVersion;
      isLoading.value = false;
    },
    () => {
      mo.value = {};
      isMOUnavailable.value = true;
      isUgc.value = true;
      isLoading.value = false;
      passiveApiFailHandler();
    },
  );
};

const renderMOAnnotation = () => {
  getMediaByAnnotation(annotation.value).then(
    (data) => {
      mo.value = data;
      moRichtext.value = data.content;
      isUgc.value = false;
      const annotated = annotation.value?.data?.highlighterSelectors?.[0];
      cuePoints.value = {
        start: Math.floor(annotated?.start / 1000),
      };
      transcriptionFileVersion.value = data.transcriptionFileVersion;
      isLoading.value = false;
    },
    () => {
      mo.value = {};
      isMOUnavailable.value = true;
      isUgc.value = false;
      isLoading.value = false;
      passiveApiFailHandler();
    },
  );
};

const handleToggleAnnotationComment = (toggleStatus) => {
  handleToggleComment(annotation.value.uuid, toggleStatus);
};

const handleDelete = () => {
  modal.removeAll();
  const modalProps = {
    title: t("annotation_delete_title"),
    content: t("annotation_delete_content"),
    primaryBtnLabel: t("button_assignment_ok"),
    secondaryBtnLabel: t("button_assignment_cancel"),
    actionPrimary: handleDeleteAnnotation,
  };
  modal.showMessage("success", modalProps);
};

const consumeOpenAnnotationQuery = () => {
  if (!route.query.openAnnotation) {
    return;
  }

  const query = { ...route.query };
  delete query.openAnnotation;
  router.replace({ query });
};

const handleClose = () => {
  if (isEditMode.value || isCreateMode.value) {
    annotationEdit.value?.handleCancel();
  } else {
    actionHidden();
  }
};

const handleDeleteAnnotation = () => {
  deleteAnnotation(annotation.value.uuid).then(() => {
    setSelectedAnnotation({
      annotation: annotation.value,
      mode: ANNOTATION_MODE.DELETE,
    });
    nextTick(() => {
      setSelectedAnnotation({
        annotation: null,
        mode: ANNOTATION_MODE.VIEW,
      });
    });
    actionHidden();
    toast.show("success", {
      title: t("annotation_delete_toast_message"),
    });
  }, passiveApiFailHandler); //OK
};

const handleAddToNotebook = () => {
  contentSubpageInstance.show(AddToNotebookSubpage, {
    baseTitle: [...titles.value],
    hasParentSubpage: true,
    annotationId: annotation.value.id,
  });
};

onMounted(() => {
  consumeOpenAnnotationQuery();
  titles.value.push(t("annotation_card_detail_view_title"));
  loadMediaAnnotation();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

$transcript-timestamp-width: 3.5rem;

.annotation-list-item-content-subpage {
  .annotation-content {
    @include media-hover();
    position: relative;

    .header-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: $spacing-03;

      > .actions {
        position: unset;
      }
    }

    .name {
      &.learning-fa {
        color: $ai;
      }
    }

    .actions {
      position: absolute;
      right: 0;
      top: 0;
      display: flex;
      align-items: center;
      gap: $spacing-03;

      .bx--overflow-menu__icon {
        fill: $interactive-01;
      }

      .sls-icon {
        [fill] {
          fill: $interactive-01;
        }
      }
    }

    .note {
      margin-bottom: $spacing-05;
    }

    .content {
      // Highlight only the text instead of the whole content background
      background-color: transparent;

      .highlight-content {
        display: flex;
        align-items: flex-start;
        gap: $spacing-03; // space between timestamp and caption
        padding: $spacing-02 0; // space between highlight and timestamp + caption
        > .media-component {
          width: 100%;
          min-width: 0;
        }
        .bx--tag {
          @include carbon--type-style("label-01");
          display: block;
          margin: 0;
          padding: $spacing-02 $spacing-04;
          min-width: $transcript-timestamp-width;
          max-width: $transcript-timestamp-width;
          background-color: tint($interactive-01, 80%);
          color: $tag-text-01;
        }
        > .rich-text {
          @include line-clamp(42);
          border-radius: 0;
        }
        .bx--tooltip__label {
          color: $text-01;
        }
      }

      span {
        &:not(.plyr__pin) {
          &.highlight-1 {
            background-color: $annotation-01;
          }

          &.highlight-2 {
            background-color: $annotation-02;
          }

          &.highlight-3 {
            background-color: $annotation-03;
          }

          &.assess-1 {
            background: $assess-01;
          }

          &.assess-2 {
            background: $assess-02;
          }

          &.assess-3 {
            background: $assess-03;
            &.highlight-2 {
              background: $assess-03-highlight-02;
            }
          }
        }

        > p {
          display: inline;
        }
      }

      .is-image-annotation {
        span {
          &.assess-1 {
            background: $assess-02;
          }

          &.assess-2 {
            background: $assess-01;
          }
        }
      }
    }

    .keywords {
      > label {
        @include carbon--type-style("label-01", true);
        color: $text-02;
      }
    }

    .name {
      @include carbon--type-style("productive-heading-01", true);
      max-width: calc(100% - 2.5rem);
    }

    .timestamp {
      flex: 0 0 100%;
      margin-top: $spacing-02;
      margin-bottom: 0;
      color: $text-02;

      .timestamp-wrapper {
        display: flex;
        align-items: center;
        margin-bottom: 0;

        .visible-to-students-tag {
          margin-right: $spacing-02;
        }

        .team-tag {
          margin-right: $spacing-03;
        }
      }

      .label {
        margin-right: $spacing-02;
        @include carbon--type-style("label-01", true);
      }
      time {
        @include carbon--type-style("label-03", true);
      }
    }
  }

  .tooltip-content {
    margin-bottom: $spacing-05;
    padding-bottom: $spacing-05;
    border-bottom: $divider-01;
    .title {
      @include carbon--type-style("label-03", true);
      margin-bottom: $spacing-03;
    }
    .description {
      @include carbon--type-style("body-short-01");
    }
  }

  .comment-section {
    padding-top: 0;
  }

  .bx--modal-container {
    .content {
      .sub {
        padding: 0 $spacing-05;

        .annotation-content {
          margin-bottom: $spacing-07;
        }

        .bx--form {
          margin-top: 0;
          .name {
            @include carbon--type-style("productive-heading-01", true);
            margin-bottom: $spacing-05;
          }

          .field-set {
            .keywords {
              input {
                background-color: $ui-background;
              }
            }
            &.visible-to-students {
              .field-input {
                display: flex;
                align-items: center;
                gap: $spacing-03;

                .bx--toggle__switch {
                  margin-top: 0;
                }
              }
            }

            &.actions {
              margin-bottom: $spacing-07;
              .field-input {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: $spacing-03;
              }
            }
          }
        }
      }
    }
  }
  .btn-add-comment {
    .sls-icon {
      margin-right: 0;
    }
  }
}
</style>
